import * as React from "react";
import { useFragment } from "react-relay";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faBadgeCheck,
	faBell,
	faChevronLeft,
	faRecordVinyl,
	faSlidersH,
	faTerminal,
	faTheaterMasks,
	faToolbox,
	faUserCircle,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { graphql } from "relay-runtime";

import { useBillingPaywall } from "~/billing";
import { AppContext } from "~/common";
import { ScrollContext } from "~/layout/ScrollContext";
import useMedia from "~/lib/hooks/useMedia";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isNative } from "~/lib/utils";
import CollapsiblePanel from "~/ui/CollapsiblePanel";
import { ButtonLink, IconButton, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import APIKeys from "../APIKeys";
import type { IntegrationSubpageKey } from "../UserIntegrations";
import DelegatedAccess from "./DelegatedAccess";
import DeveloperTools from "./DeveloperTools";
import EarlyAccess from "./EarlyAccess";
import Integrations from "./Integrations";
import MeetingCopilot from "./MeetingCopilot";
import Notifications from "./Notifications";
import Profile from "./Profile";
import UserPreferences from "./UserPreferences";

import type { SettingsModal_viewer$data, SettingsModal_viewer$key } from "~/__generated__/SettingsModal_viewer.graphql";

import styles from "./SettingsModal.module.css";

const { useCallback, useContext, useEffect, useMemo, useState, useRef } = React;

type SectionKey =
	| "profile"
	| "preferences"
	| "meeting-copilot"
	| "notifications"
	| "apps-and-integrations"
	| "delegated-access"
	| "developer"
	| "developer-tools"
	| "early-access";

interface SectionButtonProps<Key extends SectionKey> {
	currentSection: SectionKey | null;
	icon: IconProp;
	name: string;
	onSelect: (key: Key) => void;
	sectionKey: Key;
}
function SectionButton<Key extends SectionKey>({
	currentSection,
	icon,
	name,
	onSelect,
	sectionKey,
}: SectionButtonProps<Key>) {
	const onClick = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			onSelect(sectionKey);
		},
		[onSelect, sectionKey],
	);
	return (
		<button
			className={cx(styles.sectionButton, { [styles.sectionButtonSelected]: currentSection == sectionKey })}
			onClick={onClick}
		>
			<FontAwesomeIcon icon={icon} fixedWidth className={styles.sectionButtonIcon} />
			<span className={styles.sectionButtonText}>{name}</span>
		</button>
	);
}

function CloseButton({ onClick }: { onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }) {
	return <IconButton icon={faTimes} title="Close" onClick={onClick} className={styles.closeModalButton} />;
}

const settingsModalFragment = graphql`
	fragment SettingsModal_viewer on Viewer {
		activeDelegation {
			id
		}
		...BillingIdentityFeaturesPaywall_viewer
	}
`;

export interface SettingsModalProps {
	onClose: () => void;
	defaultSection?: SectionKey | ["apps-and-integrations", IntegrationSubpageKey];
	viewer: SettingsModal_viewer$key;
}

interface SidebarProps {
	section: SectionKey | null;
	setSection: (key: SectionKey | null) => void;
	isActiveDelegation: boolean;
	showTitle: boolean;
	viewer: SettingsModal_viewer$data;
}
function Sidebar({ section, setSection, isActiveDelegation, showTitle, viewer }: SidebarProps) {
	const appContext = useContext(AppContext);
	const isUXSimplificationFeatureFlagEnabled = useFeatureIsOn("ux-simplification");
	const { isPaywalled: isDeveloperApiPaywalled } = useBillingPaywall("developer_api", viewer);
	const isDeveloperApiEnabled =
		useFeatureIsOn("developer-api") && !isDeveloperApiPaywalled && appContext.account?.developerApiEnabled;

	const onSelectFellowCopilot = useCallback(
		(key: "meeting-copilot") => {
			dispatchAnalyticsTrack("[User Copilot Settings] Viewed Copilot User Settings Modal");
			setSection(key);
		},
		[setSection],
	);

	return (
		<section className={styles.sidebar}>
			{showTitle && <h1>User settings</h1>}
			<nav>
				<SectionButton
					currentSection={section}
					onSelect={setSection}
					icon={faUserCircle}
					name="Profile"
					sectionKey="profile"
				/>
				{!appContext.user?.isGuest && (
					<>
						<SectionButton
							currentSection={section}
							onSelect={setSection}
							icon={faSlidersH}
							name="Preferences"
							sectionKey="preferences"
						/>
						<SectionButton
							currentSection={section}
							onSelect={onSelectFellowCopilot}
							icon={faRecordVinyl}
							name="Note Taker"
							sectionKey="meeting-copilot"
						/>
						<SectionButton
							currentSection={section}
							onSelect={setSection}
							icon={faBell}
							name="Notifications"
							sectionKey="notifications"
						/>
						{!isNative && !isActiveDelegation && (
							<SectionButton
								currentSection={section}
								onSelect={setSection}
								icon={faToolbox}
								name="Apps and integrations"
								sectionKey="apps-and-integrations"
							/>
						)}
						{appContext.account?.allowDelegatedAccess && !isNative && !isActiveDelegation && (
							<SectionButton
								currentSection={section}
								onSelect={setSection}
								icon={faTheaterMasks}
								name="Delegated access"
								sectionKey="delegated-access"
							/>
						)}
						{window.APP_CONFIG?.apiEnabled && appContext.user?.internalAttributes?.isAccountAdmin && (
							<SectionButton
								currentSection={section}
								onSelect={setSection}
								icon={faTerminal}
								name="Developer"
								sectionKey="developer"
							/>
						)}
						{isDeveloperApiEnabled && (
							<SectionButton
								currentSection={section}
								onSelect={setSection}
								icon={faTerminal}
								name="Developer tools"
								sectionKey="developer-tools"
							/>
						)}
						{isUXSimplificationFeatureFlagEnabled && (
							<SectionButton
								currentSection={section}
								onSelect={setSection}
								icon={faBadgeCheck}
								name="Early access"
								sectionKey="early-access"
							/>
						)}
					</>
				)}
			</nav>
		</section>
	);
}

export default function SettingsModal({ defaultSection, onClose, viewer: viewerProp }: SettingsModalProps) {
	const isMobile = useMedia("(max-width: 735px)");
	const [section, setSection] = useState<SectionKey | null>(() => {
		if (typeof defaultSection === "string") {
			return defaultSection;
		} else if (typeof defaultSection === "object" && 0 in defaultSection) {
			return defaultSection[0];
		} else if (isMobile) {
			return null;
		} else {
			return "profile";
		}
	});

	useEffect(() => {
		if (section === null && isMobile === false) setSection("profile");
	}, [isMobile, section]);

	const setSectionNull = useCallback(() => setSection(null), []);

	const scrollRef = useRef<HTMLDivElement>(null);

	const viewer = useFragment(settingsModalFragment, viewerProp);
	const isActiveDelegation = Boolean(viewer.activeDelegation !== null);

	const sidebarProps = useMemo(
		() => ({
			section,
			setSection,
			isActiveDelegation,
			showTitle: !isMobile,
			viewer,
		}),
		[isActiveDelegation, isMobile, section, viewer],
	);

	const toggleSection = useCallback(value => setSection(value ? "profile" : null), []);

	const interior = (
		<section className={styles.settingsMainArea} ref={scrollRef}>
			<ScrollContext.Provider value={scrollRef.current}>
				<React.Suspense
					fallback={
						<div style={{ margin: "auto", height: "100%" }}>
							<LoadingSpinner dimmer={false} inline="centered" />
						</div>
					}
				>
					{section === "profile" ? (
						<Profile />
					) : section === "preferences" ? (
						<UserPreferences />
					) : section === "meeting-copilot" ? (
						<MeetingCopilot />
					) : section === "notifications" ? (
						<Notifications />
					) : section === "apps-and-integrations" ? (
						<Integrations
							defaultIntegration={
								Array.isArray(defaultSection) && defaultSection[0] === "apps-and-integrations"
									? defaultSection[1]
									: undefined
							}
						/>
					) : section === "delegated-access" ? (
						<DelegatedAccess />
					) : section === "developer" ? (
						<APIKeys />
					) : section === "developer-tools" ? (
						<DeveloperTools />
					) : section === "early-access" ? (
						<EarlyAccess />
					) : null}
				</React.Suspense>
			</ScrollContext.Provider>
		</section>
	);

	return (
		<Modal isOpen onClose={onClose} width="62.5rem" height="44.5rem" className={styles.modal}>
			{isMobile && (
				<Modal.Header onClose={onClose}>
					{section !== null ? (
						<ButtonLink onClick={setSectionNull}>
							<FontAwesomeIcon icon={faChevronLeft} />
							&ensp; User settings
						</ButtonLink>
					) : (
						<Modal.Title>User settings</Modal.Title>
					)}
				</Modal.Header>
			)}
			<Modal.Body className={styles.modalBody} noPadding>
				{isMobile ? (
					<CollapsiblePanel
						collapsed={section !== null}
						onChange={toggleSection}
						width="100%"
						panel={Sidebar}
						panelProps={sidebarProps}
						enabled
					>
						{interior}
					</CollapsiblePanel>
				) : (
					<>
						<Sidebar {...sidebarProps} />
						<CloseButton onClick={onClose} />
						{interior}
					</>
				)}
			</Modal.Body>
		</Modal>
	);
}
